/**
 * Ticket WK4: reine Logik der Bearbeitung direkt in der Zeile.
 *
 * Nur Lesen und Schreiben von Eingaben – kein React, keine Datenbank. Der
 * Schreibweg selbst bleibt unverändert der gemeinsame aus WK2.
 */

export type DateParts = { year: number; month: number; day: number | null };

/** Anzeige eines Datums aus Jahr, Monat und optionalem Tag. */
export function formatDateParts(parts: {
  year: number | null;
  month: number | null;
  day: number | null;
}): string {
  if (parts.year === null || parts.month === null) return "—";
  const month = String(parts.month).padStart(2, "0");
  if (parts.day === null) return `${month}/${parts.year}`;
  return `${String(parts.day).padStart(2, "0")}.${month}.${parts.year}`;
}

/**
 * Liest eine Eingabe in der Zeile: „15.03.2026", „03/2026", „3.2026" oder
 * „2026-03-15". Eine leere Eingabe ergibt null (Feld wird geleert).
 * Unlesbare oder unmögliche Angaben ergeben „ungueltig".
 */
export function parseDateInput(value: string): DateParts | null | "ungueltig" {
  const raw = value.trim();
  if (raw === "") return null;

  let year: number | null = null;
  let month: number | null = null;
  let day: number | null = null;

  const iso = /^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/u.exec(raw);
  const dotted = /^(?:(\d{1,2})\.)?\s*(\d{1,2})[./]\s*(\d{4})$/u.exec(raw);
  const monthYear = /^(\d{1,2})\s*\/\s*(\d{4})$/u.exec(raw);

  if (iso) {
    year = Number(iso[1]);
    month = Number(iso[2]);
    day = iso[3] ? Number(iso[3]) : null;
  } else if (monthYear) {
    month = Number(monthYear[1]);
    year = Number(monthYear[2]);
  } else if (dotted) {
    day = dotted[1] ? Number(dotted[1]) : null;
    month = Number(dotted[2]);
    year = Number(dotted[3]);
  } else {
    return "ungueltig";
  }

  if (month < 1 || month > 12) return "ungueltig";
  if (year < 1900 || year > 2100) return "ungueltig";
  if (day !== null) {
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    if (day < 1 || day > daysInMonth) return "ungueltig";
  }
  return { year, month, day };
}

/** Name der amtierenden Person: erstes Wort Vorname, der Rest Nachname. */
export function splitHolderName(value: string): { firstName: string | null; lastName: string | null } {
  const parts = value.trim().split(/\s+/u).filter(Boolean);
  if (parts.length === 0) return { firstName: null, lastName: null };
  if (parts.length === 1) return { firstName: null, lastName: parts[0]! };
  return { firstName: parts[0]!, lastName: parts.slice(1).join(" ") };
}

export function joinHolderName(row: { firstName: string | null; lastName: string | null }): string {
  return [row.firstName, row.lastName].filter(Boolean).join(" ").trim();
}
